import { useEffect } from 'react';
import { useUser } from '@r/api/auth.tsx';
import defaultAsset from '@r/assets/images/fiddy/default-game-cover.png';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Skeleton } from '@ui/skeleton.tsx';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { ScaleCtaCard } from '@client/components/games/scale-cta-card.tsx';
import Marquee from '@r/components/ui/marquee.tsx';
import { useRouteExists } from '@r/hooks/use-route-exists.ts';
import { useApiGamesGetCollection } from '@r/api/generated/client.ts';
import type { GameGameCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { PageContainer } from '@client/components/layout/page-container.tsx';

type Game = GameGameCollectionReadModel;

const Games = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useUser().data;
  const workspaces = UseWorkspace(user);

  const isDataAvailable = Boolean(workspaces.workspace?.id && user?.id);

  const gamesListQuery = useApiGamesGetCollection(
    { 'order[closingDate]': 'desc' },
    {
      query: {
        queryKey: ['games', workspaces.workspace?.id, user?.id],
        staleTime: Number.POSITIVE_INFINITY,
        enabled: isDataAvailable
      }
    }
  );
  useOrvalQueryErrorToast(gamesListQuery, t('toast.error.label'));

  const games = gamesListQuery.data ?? [];
  const isPending = gamesListQuery.isPending;
  const isError = gamesListQuery.isError;
  const isSuccess = gamesListQuery.isSuccess;
  const gamesList: Game[] = games;
  const activeGames = gamesList.filter((game) => game.status === 'game.status.active');
  const inactiveGames = gamesList.filter((game) => game.status !== 'game.status.active');

  const routeExists = useRouteExists('/games/$id');

  useEffect(() => {
    if (!isSuccess || !games || games.length !== 1 || !routeExists) {
      return;
    }

    const [singleGame] = games;

    if (singleGame.status === 'game.status.active') {
      void navigate({
        to: '/games/$id',
        params: { id: singleGame.id.toString() }
      });
    }
  }, [games, isSuccess, navigate, routeExists]);

  return (
    <PageContainer className="pt-10">
      <section className="flex flex-col gap-10 pb-7">
        <header>
          <h1 className="mb-6 text-2xl font-bold text-foreground md:text-3xl lg:mb-10 lg:text-[2.75rem] lg:leading-[3.5rem]">
            {t('games.title')}
          </h1>
        </header>

        {(isPending || isError) && (
          <Skeleton className="flex relative flex-col justify-center items-center space-y-6 h-60 rounded-3xl bg-muted-foreground/40">
            {isError && (
              <p className="px-6 text-base text-center text-destructive">{t('games.list.error')}</p>
            )}
            {!isError && (
              <>
                <Skeleton className="w-3/6 h-10" />
                <Skeleton className="absolute bottom-2.5 right-2.5 h-10 w-10 rounded-full" />
              </>
            )}
          </Skeleton>
        )}

        {isSuccess && gamesList.length === 0 && (
          <div className="flex justify-center">
            <div className="flex flex-col justify-center items-center w-full h-60 text-center rounded-3xl border border-dashed border-muted-foreground/40 md:max-w-[calc(50%-0.625rem)] lg:max-w-[calc(33.333%-0.833rem)]">
              <p className="text-lg font-medium">{t('games.empty.title')}</p>
              <p className="mt-1 text-base text-muted-foreground">{t('games.empty.description')}</p>
            </div>
          </div>
        )}

        {isSuccess && activeGames.length > 0 && (
          <section className="space-y-6">
            <header>
              <h2 className="text-xl font-semibold text-foreground">
                {t('games.section.available')}
              </h2>
            </header>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {activeGames.map((game) => {
                const getFormattedDate = (date?: string) =>
                  date ? format(new Date(date), 'dd MMM yyyy', { locale: fr }) : undefined;

                const statusLabel = (() => {
                  const closingDate = getFormattedDate(game.closingDate);
                  return closingDate
                    ? t('games.status.active.with.date', { date: closingDate })
                    : t('games.status.active');
                })();

                return (
                  <ScaleCtaCard
                    key={game.id}
                    to="/games/$id"
                    params={{ id: game.id.toString() }}
                    imageSrc={game.cover?.pathUri || defaultAsset}
                    imageAlt={game.label}
                    title={game.label}
                    ctaLabel={t('games.cta.play')}
                    ctaPosition="center"
                    defaultImage={defaultAsset}
                    statusLabel={statusLabel}
                  />
                );
              })}
            </div>
          </section>
        )}

        {isSuccess && inactiveGames.length > 0 && (
          <section className="space-y-6">
            <header>
              <h2 className="text-xl font-semibold text-foreground">
                {t('games.section.upcoming.finished')}
              </h2>
            </header>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {inactiveGames.map((game) => {
                const getFormattedDate = (date?: string) =>
                  date ? format(new Date(date), 'dd MMM yyyy', { locale: fr }) : undefined;

                const statusLabel = (() => {
                  if (game.status === 'game.status.planned') {
                    const openingDate = getFormattedDate(game.openingDate);
                    return openingDate
                      ? t('games.status.planned.with.date', { date: openingDate })
                      : t('games.status.planned');
                  }

                  if (game.status === 'game.status.finished') {
                    const closingDate = getFormattedDate(game.closingDate);
                    return closingDate
                      ? t('games.status.finished.with.date', { date: closingDate })
                      : t('games.status.finished');
                  }

                  return undefined;
                })();

                return (
                  <ScaleCtaCard
                    key={game.id}
                    imageSrc={game.cover?.pathUri || defaultAsset}
                    imageAlt={game.label}
                    title={game.label}
                    defaultImage={defaultAsset}
                    disabled={true}
                    statusLabel={statusLabel}
                  />
                );
              })}
            </div>
          </section>
        )}
      </section>

      <Marquee text={t('games.animated.text')} />
    </PageContainer>
  );
};

export { Games };
